import { Avatar, Box, Typography } from '@mui/material';
import { useFormContext, useWatch } from 'react-hook-form';

import type { UserCard_UserFragment } from '@graphql/__generated__/graphql';
import type { TListedByFormData } from '@modules/_shared/components/listing-overview-form/listingOverviewFormDefinition';
import { SegmentedToggleButtonGroup } from '@modules/_shared/components/SegmentedToggleButtonGroup';
import { formatPhone } from '@modules/_shared/utils/formatting';
import { useLocale } from '@shared/hooks/locale';

interface FsboContactCardProps {
  seller: UserCard_UserFragment | null;
}

const initialsOf = (name: string): string =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part.charAt(0).toUpperCase())
    .join('');

// Tabs + live preview at the top of the FSBO "Listed by" dialog. The segmented
// control drives use_dummy_contact (Advertiser vs. a manually entered contact)
// and the card previews exactly what publishes on the listing, honouring the
// fsbo_hide_phone / fsbo_hide_last_name toggles below.
export const FsboContactCard: React.FC<FsboContactCardProps> = ({ seller }) => {
  const { t } = useLocale();
  const { control, setValue } = useFormContext<TListedByFormData>();

  const useDummyContact =
    useWatch({ control, name: 'use_dummy_contact' }) === true;
  const hidePhone = useWatch({ control, name: 'fsbo_hide_phone' }) === true;
  const hideLastName =
    useWatch({ control, name: 'fsbo_hide_last_name' }) === true;
  const dummyName = useWatch({ control, name: 'dummy_broker_name' });
  const dummyPhone = useWatch({ control, name: 'dummy_broker_phone' });

  const firstName = seller?.first_name?.trim() ?? '';
  const lastName = seller?.last_name?.trim() ?? '';
  const sellerFullName =
    seller?.full_name?.trim() ||
    [firstName, lastName].filter(Boolean).join(' ');
  const sellerShortName =
    firstName && lastName
      ? `${firstName} ${lastName.charAt(0).toUpperCase()}.`
      : firstName || lastName || sellerFullName;
  // Mirror the IDX resolver (verified only, proxy first) so the preview
  // matches what actually publishes.
  const verifiedPhones =
    seller?.phone_numbers?.filter(p => p.verified === true) ?? [];
  const sellerPhone =
    verifiedPhones.find(p => p.type === 'proxy')?.number ??
    verifiedPhones[0]?.number ??
    '';

  // What the listing actually shows for the resolved choice.
  const previewName = useDummyContact
    ? (dummyName ?? '').trim()
    : hideLastName
    ? sellerShortName
    : sellerFullName;
  const previewPhone = useDummyContact
    ? (dummyPhone ?? '').trim()
    : sellerPhone;
  const phoneHidden = !useDummyContact && hidePhone;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <SegmentedToggleButtonGroup
        fullWidth
        value={useDummyContact ? 'other' : 'me'}
        onChange={value => {
          setValue('use_dummy_contact', value === 'other', {
            shouldDirty: true,
            shouldValidate: true,
          });
        }}
        options={[
          { value: 'me', label: t('Advertiser') },
          { value: 'other', label: t('Someone else') },
        ]}
        sx={theme => ({
          bgcolor: (theme.vars ?? theme).palette.action.hover,
          borderRadius: 2,
          p: 0.5,
          gap: 0.5,
          '& .MuiToggleButtonGroup-grouped': {
            border: 0,
            borderRadius: `${
              Number(theme.shape.borderRadius) * 1.5
            }px !important`,
            py: 0.75,
            fontWeight: 600,
            textTransform: 'none',
            color: 'text.secondary',
            '&:hover': { bgcolor: 'transparent' },
            '&.Mui-selected': {
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              boxShadow: 1,
              '&:hover': { bgcolor: 'primary.dark' },
            },
          },
        })}
      />

      <Box
        sx={theme => ({
          border: `1px solid ${(theme.vars ?? theme).palette.divider}`,
          borderRadius: 1,
          p: 1.5,
          bgcolor: 'background.paper',
        })}
      >
        <Typography
          variant="caption"
          sx={{
            color: 'neutral.main',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: 0.4,
          }}
        >
          {t('Shown on the listing')}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1 }}>
          <Avatar sx={{ width: 44, height: 44 }}>
            {previewName ? initialsOf(previewName) : '?'}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {previewName || t('Name not set')}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'neutral.main',
                fontStyle: phoneHidden ? 'italic' : 'normal',
              }}
            >
              {phoneHidden
                ? t('Phone number not shown')
                : (formatPhone(previewPhone) ?? previewPhone) ||
                  t('Phone number not set')}
            </Typography>
          </Box>
        </Box>
        <Typography
          variant="caption"
          sx={{ color: 'neutral.main', display: 'block', mt: 1 }}
        >
          {t('Enquiries always reach the advertiser by email.')}
        </Typography>
      </Box>
    </Box>
  );
};
